import React, { useState } from "react";
import {
  Badge,
  Box,
  Paper,
  Stack,
  styled,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { CustomTabPanel } from "../../../shared";
import arrowIcon from "../../../assets/images/Icons/Vector (9).svg";
import { Batches, Recipes } from "../components";
import { formatWeightWithUnits } from "../../../utils";

const StyledBadge = styled(Badge)(({ active }) => ({
  "& .MuiBadge-badge": {
    right: 4,
    top: 10,
    padding: "0 4px",
    position: "relative",
    background: active ? "#FFFFFF" : "#263141",
    color: active ? "#263141" : "#FFFFFF",
  },
}));

export const CommonTab = ({
  state,
  templates,
  handleOptionChangeRecipe,
  handleOptionChangeRecipeBatch,
  globalData,
  setTemplates,
}) => {
  const [value, setValue] = useState(0);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    setTemplates((draft) => {
      draft.indexColumn = 0;
      draft.isTabChange = true;
    });
  };

  const batchesLength = state?.data?.batches?.length || 0;
  const recipesLength = state?.data?.other_recipes?.length || 0;

  return (
    <Stack
      sx={{
        flexDirection: "row",
        height: "calc(90vh - 128px)",
      }}
    >
      <Stack
        sx={{
          backgroundColor: "background.default",
          width: { md: value === 0 ? "290px" : "331px", xs: "100%" },
          transition: "width 0.5s",
        }}
      >
        <Tabs
          className="styled-tabs"
          value={value}
          onChange={handleChange}
          sx={{
            marginLeft: "0",
            mx: "8px",
            mt: "8px",
          }}
        >
          <Tab
            label={
              <Stack direction="row" gap="10px">
                Batches
                <StyledBadge
                  badgeContent={batchesLength}
                  active={value === 0}
                />
              </Stack>
            }
          />
          <Tab
            label={
              <Stack direction="row" gap="10px">
                Products
                <StyledBadge
                  badgeContent={recipesLength}
                  active={value === 1}
                />
              </Stack>
            }
          />
        </Tabs>

        <Stack
          height="34px"
          justifyContent="space-between"
          alignItems="center"
          display="flex"
          flexDirection="row"
          px="8px"
        >
          <Typography fontWeight={600} fontSize="12px">
            Recipe Name
          </Typography>

          {value === 1 && (
            <Typography fontWeight={600} fontSize="12px">
              Quantity
            </Typography>
          )}
        </Stack>

        <Stack
          height="calc(90vh - 140px)"
          px="8px"
          pb="8px"
          sx={{ overflowY: "auto" }}
        >
          <Stack
            borderColor="divider"
            component={Paper}
            borderRadius={0}
            elevation={0}
          >
            {value === 0 &&
              state?.data?.batches?.map((item, index) => {
                const isSelected = index === templates.indexColumn;
                const lastItem = state?.data?.batches.length - 1;
                return (
                  <Stack
                    sx={{
                      minHeight: "30px",
                      color: isSelected ? "#FFFFFF" : "text.primary",
                      backgroundColor: isSelected ? "primary.main" : "inherit",
                      borderBottom: lastItem === index ? 0 : 1,
                      borderColor: "divider",
                      padding: "8px 12px",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: (theme) =>
                          !isSelected
                            ? theme.palette.action.hover
                            : "primary.main",
                      },
                    }}
                    onClick={() => handleOptionChangeRecipeBatch(item, index)}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      flexWrap="wrap"
                    >
                      <Stack alignItems="flex-start">
                        <Typography
                          fontWeight={500}
                          fontSize="14px"
                          alignContent="center"
                          sx={{
                            overflowWrap: "break-word",
                            wordBreak: "break-word",
                            whiteSpace: "normal",
                          }}
                        >
                          {item?.recipe_name}
                        </Typography>
                        <Typography fontSize="12px" fontWeight={400}>
                          {formatWeightWithUnits(
                            item?.total_weight,
                            item?.total_weight_unit,
                            globalData?.all_Units
                          )}
                        </Typography>
                      </Stack>
                      <Stack direction="row" gap={1} alignItems="center">
                        <img
                          alt="details"
                          src={arrowIcon}
                          style={{
                            height: "10px",
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                );
              })}

            {value === 1 &&
              state?.data?.other_recipes?.map((item, index) => {
                const isSelected = index === templates.indexColumn;
                const lastItem = state?.data?.other_recipes - 1;
                return (
                  <Stack
                    sx={{
                      minHeight: "30px",
                      color: isSelected ? "#FFFFFF" : "#263141",
                      backgroundColor: isSelected ? "primary.main" : "inherit",
                      cursor: "pointer",
                      borderBottom: lastItem === index ? 0 : 1,
                      borderColor: "divider",
                      padding: "8px 12px",
                    }}
                    onClick={() => handleOptionChangeRecipe(item, index)}
                  >
                    <Stack direction="row" justifyContent="space-between">
                      <Typography
                        fontWeight={400}
                        fontSize="14px"
                        alignContent="center"
                        sx={{
                          overflowWrap: "break-word",
                          wordBreak: "break-word",
                          whiteSpace: "normal",
                        }}
                      >
                        {item?.recipe_name}
                      </Typography>

                      <Stack
                        direction="row"
                        gap="8px"
                        alignItems="center"
                        flexShrink={0}
                      >
                        <Typography fontSize="14px" fontWeight={500}>
                          {formatWeightWithUnits(
                            item?.total_quantity,
                            item?.selling_unit_name,
                            globalData?.all_Units
                          )}
                        </Typography>

                        <img
                          alt="details"
                          src={arrowIcon}
                          style={{
                            height: "10px",
                          }}
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                );
              })}
          </Stack>
        </Stack>
      </Stack>

      <Box flex={1}>
        {/* Ensure full height usage */}
        <CustomTabPanel value={value} index={0} border={false}>
          <Batches
            selectedOption={templates?.templateBatchItems}
            templates={templates}
            globalData={globalData}
          />
        </CustomTabPanel>
        <CustomTabPanel
          value={value}
          index={1}
          border={false}
          style={{
            paddingInline: "12px",
            paddingBottom: "12px",
            overflowY: "auto",
            height: "calc(90vh - 128px)",
          }}
        >
          <Recipes
            selectedOptionRecipe={templates?.templateItems}
            globalData={globalData}
          />
        </CustomTabPanel>
      </Box>
    </Stack>
  );
};
